import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { Plus, Lock, Square, Pencil, RefreshCw, Search, Eraser, ArrowLeft, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowUpDown, HelpCircle, X, Calendar, ShieldCheck, Clock } from 'lucide-react';
import { DateTimePicker } from '@/shared/components/DateTimePicker.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useMaintenanceList,
  useCreateMaintenance,
  useUpdateMaintenance,
  useDeleteMaintenance,
} from '../hooks/useMaintenances.ts';
import { useActiveTenants } from '@/shared/hooks/useTenantsSearch.ts';
import { TenantMultiSelect } from '@/shared/components/TenantMultiSelect.tsx';
import { ConfirmModal } from '@/shared/components/ConfirmModal.tsx';
import type { CreateMaintenanceDTO } from '../api/maintenances.api.ts';
import type { MaintenanceRow, MaintenanceStatusFilter, MaintenanceListParams } from '@/shared/types/maintenance.ts';

/* ── constants ────────────────────────────────────────── */

const STATUS_TABS: { key: MaintenanceStatusFilter; label: string }[] = [
  { key: 'upcoming', label: 'Agendadas' },
  { key: 'active', label: 'Em andamento' },
  { key: 'closed', label: 'Encerradas' },
];

const TIPO_LABELS: Record<string, string> = {
  AGENDADA: 'Agendada',
  RETROATIVA: 'Retroativa',
};

const columnHelper = createColumnHelper<MaintenanceRow>();

/* ── helpers ──────────────────────────────────────────── */

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function calcDuration(start: string, end: string): string {
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remMins}min`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function formatCountdown(iniciadoEm: string, now: number): string {
  const start = new Date(iniciadoEm).getTime();
  const diff = start - now;
  if (diff <= 0) return 'Em andamento';
  const mins = Math.floor(diff / 60_000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (days > 0) return `Em ${days}d ${hrs % 24}h`;
  return `Em ${hrs}h ${mins % 60}min`;
}

/* ── form schema ─────────────────────────────────────── */

const formSchema = z
  .object({
    titulo: z.string().min(2, 'Título deve ter no mínimo 2 caracteres'),
    descricao: z.string().optional(),
    tipo: z.enum(['AGENDADA', 'RETROATIVA']),
    iniciadoEm: z.string().min(1, 'Data de início é obrigatória'),
    encerradoEm: z.string().min(1, 'Data de término é obrigatória'),
  })
  .refine((d) => new Date(d.encerradoEm) > new Date(d.iniciadoEm), {
    message: 'Data de término deve ser posterior à data de início',
    path: ['encerradoEm'],
  });

type FormFields = z.infer<typeof formSchema>;

/* ── countdown hook ───────────────────────────────────── */

function useMinuteTick(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/* ── types ────────────────────────────────────────────── */

type ViewMode = 'list' | 'form';

/* ── Modal: Como funcionam as manutenções ──────────────── */

function MaintenanceHelpModal({ onClose }: { onClose: () => void }) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative mx-4 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <HelpCircle size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Como funcionam as manutenções?</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* Impacto no SLA */}
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <ShieldCheck size={16} className="text-green-600 dark:text-green-400" />
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">Impacto no Cálculo de SLA</h4>
            </div>
            <div className="space-y-1.5 text-xs text-green-700 dark:text-green-300/90">
              <p>Manutenções cadastradas são <strong>excluídas do cálculo de uptime</strong> (Cláusula 10 do SLA).</p>
              <p>Isso significa que o período de manutenção <strong>não conta como indisponibilidade</strong>, mesmo que o sistema esteja fora do ar durante a janela.</p>
            </div>
          </div>

          {/* Tipos */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Calendar size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Tipos de Manutenção</h4>
            </div>
            <div className="space-y-2 text-xs text-blue-700 dark:text-blue-300/90">
              <div>
                <strong>Agendada (futura):</strong> manutenção planejada com janela de início e término no futuro. Aparece na página pública como "Manutenção Programada".
              </div>
              <div>
                <strong>Retroativa (já ocorreu):</strong> manutenção registrada após ter acontecido. Útil para corrigir o cálculo de SLA quando houve uma manutenção não planejada.
              </div>
            </div>
          </div>

          {/* Ciclo de vida */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="mb-2.5 flex items-center gap-2">
              <Clock size={16} className="text-gray-600 dark:text-gray-400" />
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ciclo de Vida</h4>
            </div>
            <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
              <p><strong>Agendada</strong> → aguardando a janela de início</p>
              <p><strong>Em andamento</strong> → dentro da janela (início atingido)</p>
              <p><strong>Encerrada</strong> → finalizada manualmente ou ao atingir o término</p>
              <p className="mt-2 text-[11px] italic text-gray-500">Após encerrada, a manutenção se torna imutável (não pode ser editada ou excluída).</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── main component ───────────────────────────────────── */

export function MaintenanceListPage() {
  // ── State ─────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showHelp, setShowHelp] = useState(false);
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatusFilter>('upcoming');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'iniciadoEm', desc: true }]);
  const [searched, setSearched] = useState(false);

  const [editingMaintenance, setEditingMaintenance] = useState<MaintenanceRow | null>(null);
  const [closingMaintenance, setClosingMaintenance] = useState<MaintenanceRow | null>(null);

  const now = useMinuteTick();

  // ── Queries ───────────────────────────────────────────
  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      status: statusFilter,
      sortBy: (sorting[0]?.id as MaintenanceListParams['sortBy']) || 'iniciadoEm',
      sortOrder: (sorting[0]?.desc ? 'desc' : 'asc') as MaintenanceListParams['sortOrder'],
    }),
    [pagination.pageIndex, pagination.pageSize, statusFilter, sorting],
  );

  const { data: response, isLoading } = useMaintenanceList(params, searched);
  const { data: activeTenants } = useActiveTenants();

  const createMutation = useCreateMaintenance(() => setViewMode('list'));
  const updateMutation = useUpdateMaintenance(() => {
    setViewMode('list');
    setEditingMaintenance(null);
  });
  const deleteMutation = useDeleteMaintenance(() => setClosingMaintenance(null));

  // ── Form state ────────────────────────────────────────
  const isEdit = !!editingMaintenance;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [tenantError, setTenantError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      titulo: '',
      descricao: '',
      tipo: 'AGENDADA',
      iniciadoEm: '',
      encerradoEm: '',
    },
  });

  const tipo = watch('tipo');

  // ── Date picker state ──────────────────────────────────
  const [iniciadoEmDate, setIniciadoEmDate] = useState<Date | null>(null);
  const [encerradoEmDate, setEncerradoEmDate] = useState<Date | null>(null);

  function handleIniciadoEmChange(d: Date | null) {
    setIniciadoEmDate(d);
    setValue('iniciadoEm', d ? d.toISOString() : '', { shouldValidate: true });
  }

  function handleEncerradoEmChange(d: Date | null) {
    setEncerradoEmDate(d);
    setValue('encerradoEm', d ? d.toISOString() : '', { shouldValidate: true });
  }

  // Reset form when editingMaintenance changes or when switching to form view
  useEffect(() => {
    if (viewMode === 'form') {
      if (editingMaintenance) {
        reset({
          titulo: editingMaintenance.titulo,
          descricao: editingMaintenance.descricao ?? '',
          tipo: editingMaintenance.tipo,
          iniciadoEm: editingMaintenance.iniciadoEm,
          encerradoEm: editingMaintenance.encerradoEm,
        });
        setIniciadoEmDate(new Date(editingMaintenance.iniciadoEm));
        setEncerradoEmDate(new Date(editingMaintenance.encerradoEm));
      } else {
        reset({
          titulo: '',
          descricao: '',
          tipo: 'AGENDADA',
          iniciadoEm: '',
          encerradoEm: '',
        });
        setIniciadoEmDate(null);
        setEncerradoEmDate(null);
      }
      setSelectedIds([]);
      setSelectAll(false);
      setTenantError('');
    }
  }, [viewMode, editingMaintenance, reset]);

  // ── Tenant selection handlers ─────────────────────────
  function handleToggle(id: string) {
    setTenantError('');
    if (selectAll) {
      setSelectAll(false);
      setSelectedIds((activeTenants ?? []).filter((t) => t.id !== id).map((t) => t.id));
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    }
  }

  function handleSelectAll() {
    setTenantError('');
    setSelectAll(true);
    setSelectedIds([]);
  }

  function handleClearAll() {
    setSelectAll(false);
    setSelectedIds([]);
  }

  // ── Form submit ───────────────────────────────────────
  function onFormSubmit(data: FormFields) {
    if (isEdit && editingMaintenance) {
      updateMutation.mutate({
        id: editingMaintenance.id,
        input: {
          titulo: data.titulo,
          descricao: data.descricao || undefined,
          iniciadoEm: new Date(data.iniciadoEm).toISOString(),
          encerradoEm: new Date(data.encerradoEm).toISOString(),
        },
      });
      return;
    }

    if (!selectAll && selectedIds.length === 0) {
      setTenantError('Selecione ao menos um tenant');
      return;
    }

    const payload: CreateMaintenanceDTO = {
      titulo: data.titulo,
      descricao: data.descricao || undefined,
      tipo: data.tipo,
      iniciadoEm: new Date(data.iniciadoEm).toISOString(),
      encerradoEm: new Date(data.encerradoEm).toISOString(),
    };

    if (selectAll) {
      payload.selectAll = true;
    } else {
      payload.tenantIds = selectedIds;
    }

    createMutation.mutate(payload);
  }

  // ── Filter handlers ───────────────────────────────────
  function handleStatusTabChange(tab: MaintenanceStatusFilter) {
    setStatusFilter(tab);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }

  function handleSearch() {
    setSearched(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function handleClear() {
    setStatusFilter('upcoming');
    setSorting([{ id: 'iniciadoEm', desc: true }]);
    setSearched(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  // ── Columns ───────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        header: 'Ações',
        enableSorting: false,
        cell: ({ row }) => {
          const m = row.original;
          if (m.imutavelApos) {
            return <Lock size={16} className="text-gray-400" />;
          }
          const isActiveRow = !m.fechadoEm && new Date(m.iniciadoEm).getTime() <= now;
          const isUpcoming = !m.fechadoEm && new Date(m.iniciadoEm).getTime() > now;
          return (
            <div className="flex items-center gap-1">
              {(isUpcoming || isActiveRow) && (
                <button
                  title="Editar"
                  onClick={() => {
                    setEditingMaintenance(m);
                    setViewMode('form');
                  }}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                >
                  <Pencil size={15} />
                </button>
              )}
              {isActiveRow && (
                <button
                  title="Encerrar"
                  onClick={() => setClosingMaintenance(m)}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <Square size={16} />
                </button>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('titulo', {
        header: 'Título',
        cell: (info) => (
          <span className="max-w-[200px] truncate font-medium">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('tipo', {
        header: 'Tipo',
        cell: (info) => (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {TIPO_LABELS[info.getValue()] ?? info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('tenants', {
        header: 'Tenants Afetados',
        enableSorting: false,
        cell: (info) => {
          const tenants = info.getValue();
          if (tenants.length === 0) return <span className="text-gray-400">—</span>;
          const show = tenants.slice(0, 3);
          const rest = tenants.length - show.length;
          return (
            <div className="flex flex-wrap gap-1">
              {show.map((t) => (
                <span
                  key={t.tenantId}
                  className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                >
                  {t.nome}
                </span>
              ))}
              {rest > 0 && (
                <span className="inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-600 dark:text-gray-400">
                  +{rest}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'janela',
        header: 'Janela (GMT-3)',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-xs">
            {fmtDate(row.original.iniciadoEm)} → {fmtDate(row.original.encerradoEm)}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'duracao',
        header: 'Duração',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {calcDuration(row.original.iniciadoEm, row.original.encerradoEm)}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const m = row.original;
          if (m.fechadoEm) {
            return (
              <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                Encerrada
              </span>
            );
          }
          const startMs = new Date(m.iniciadoEm).getTime();
          if (startMs > now) {
            return (
              <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {formatCountdown(m.iniciadoEm, now)}
              </span>
            );
          }
          return (
            <span className="inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
              Em andamento
            </span>
          );
        },
      }),
    ],
    [now],
  );

  // ── Table ─────────────────────────────────────────────
  const table = useReactTable({
    data: response?.data ?? [],
    columns,
    pageCount: response ? Math.ceil(response.total / response.limit) : -1,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  const isSubmitting = isEdit ? updateMutation.isPending : createMutation.isPending;

  // ── VIEW: Form (Create / Edit) ────────────────────────
  if (viewMode === 'form') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => { setViewMode('list'); setEditingMaintenance(null); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="border-b-2 border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-xl font-semibold">{isEdit ? 'Editar Manutenção' : 'Nova Manutenção'}</h2>
          </div>
          <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-5 px-6 py-6">
            {/* Toggle tipo (apenas criação) */}
            {!isEdit && (
              <div>
                <label className="mb-1.5 block text-sm font-medium">Tipo</label>
                <div className="flex rounded-md border border-gray-300 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => setValue('tipo', 'AGENDADA')}
                    className={`flex-1 rounded-l-md px-4 py-2 text-sm font-medium transition-colors ${
                      tipo === 'AGENDADA'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Agendada (futura)
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('tipo', 'RETROATIVA')}
                    className={`flex-1 rounded-r-md px-4 py-2 text-sm font-medium transition-colors ${
                      tipo === 'RETROATIVA'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Retroativa (já ocorreu)
                  </button>
                </div>
              </div>
            )}

            {/* Título */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">Título</label>
              <input
                {...register('titulo')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Descreva a manutenção"
              />
              {errors.titulo && <p className="mt-1 text-xs text-red-500">{errors.titulo.message}</p>}
            </div>

            {/* Descrição */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Descrição <span className="text-gray-400">(opcional)</span>
              </label>
              <textarea
                {...register('descricao')}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Detalhes adicionais..."
              />
            </div>

            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Início</label>
                <DateTimePicker
                  value={iniciadoEmDate}
                  onChange={handleIniciadoEmChange}
                  maxDate={encerradoEmDate ?? undefined}
                  placeholder="dd/mm/aaaa hh:mm"
                />
                {errors.iniciadoEm && (
                  <p className="mt-1 text-xs text-red-500">{errors.iniciadoEm.message}</p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Término</label>
                <DateTimePicker
                  value={encerradoEmDate}
                  onChange={handleEncerradoEmChange}
                  minDate={iniciadoEmDate ?? undefined}
                  placeholder="dd/mm/aaaa hh:mm"
                />
                {errors.encerradoEm && (
                  <p className="mt-1 text-xs text-red-500">{errors.encerradoEm.message}</p>
                )}
              </div>
            </div>

            {/* TenantMultiSelect (apenas criação) */}
            {!isEdit && (
              <>
                <TenantMultiSelect
                  tenants={activeTenants ?? []}
                  selected={selectedIds}
                  selectAll={selectAll}
                  onToggle={handleToggle}
                  onSelectAll={handleSelectAll}
                  onClearAll={handleClearAll}
                />
                {tenantError && <p className="text-xs text-red-500">{tenantError}</p>}
              </>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setViewMode('list');
                  setEditingMaintenance(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting
                  ? isEdit
                    ? 'Salvando...'
                    : 'Criando...'
                  : isEdit
                    ? 'Salvar'
                    : 'Criar Manutenção'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── VIEW: List ────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Manutenções</h1>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            Agende e gerencie janelas de manutenção excluídas do cálculo de SLA
            <button
              onClick={() => setShowHelp(true)}
              className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
              title="Como funcionam as manutenções?"
            >
              <HelpCircle size={16} />
            </button>
          </p>
        </div>
        <button
          onClick={() => { setEditingMaintenance(null); setViewMode('form'); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} />
          Nova Manutenção
        </button>
      </div>

      {/* Filter panel */}
      <div className="rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        {/* Status filter */}
        <div className="mb-4">
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
          <div className="flex items-center gap-4">
            {STATUS_TABS.map((tab) => (
              <label key={tab.key} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="maintenanceStatusFilter"
                  value={tab.key}
                  checked={statusFilter === tab.key}
                  onChange={() => handleStatusTabChange(tab.key)}
                  className="h-4 w-4 cursor-pointer accent-blue-600"
                />
                {tab.label}
              </label>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            <Search size={16} />
            Pesquisar
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 rounded-md bg-gray-400 px-4 py-2 text-sm font-medium text-white hover:bg-gray-500"
          >
            <Eraser size={16} />
            Limpar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {!searched ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <Search size={24} className="mb-1" />
            <p className="text-sm font-medium">Utilize os filtros acima e clique em Pesquisar</p>
          </div>
        ) : isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : response && response.data.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <p className="text-sm font-medium">Nenhuma manutenção encontrada.</p>
            <p className="text-xs">Cadastre uma nova manutenção ou ajuste os filtros de busca.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr
                  key={hg.id}
                  className="border-b-2 border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400"
                >
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 ${canSort ? 'cursor-pointer select-none' : ''}`}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            header.column.getIsSorted() === 'asc'
                              ? <ArrowUpNarrowWide size={14} className="text-blue-600 dark:text-blue-400" />
                              : header.column.getIsSorted() === 'desc'
                                ? <ArrowDownWideNarrow size={14} className="text-blue-600 dark:text-blue-400" />
                                : <ArrowUpDown size={14} className="text-gray-300 dark:text-gray-600" />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={`border-b border-gray-100 transition-colors hover:bg-blue-50/30 dark:border-gray-800 dark:hover:bg-blue-900/10 ${Number(row.id) % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {searched && response && response.total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
            <span className="text-gray-500 dark:text-gray-400">
              Mostrando {Math.min(pagination.pageIndex * pagination.pageSize + 1, response.total)} a{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, response.total)} de {response.total} registros
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <span className="text-xs">Resultados por página:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Anterior
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                  {pagination.pageIndex + 1} / {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de encerramento */}
      {closingMaintenance && (
        <ConfirmModal
          title="Encerrar Manutenção"
          message={
            <>
              Deseja encerrar a manutenção <strong>{closingMaintenance.titulo}</strong>?
              Esta ação tornará o registro imutável.
            </>
          }
          confirmLabel="Encerrar"
          onConfirm={() => deleteMutation.mutate(closingMaintenance.id)}
          onCancel={() => setClosingMaintenance(null)}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Modal de ajuda */}
      {showHelp && <MaintenanceHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
